import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthService from "../services/auth.service";
import api from "../services/api";
import { describe } from "../constants/diseases";
import { isDesktopViewport } from "../utils/viewport";
import Modal from "./UI/Modal";
import DiseaseForm from "./forms/DiseaseForm";
import { ListCard, EmptyState, ListRow, ChevronIcon } from "./UI/ListCard";

function StatusBadge({ healed }) {
  return healed ? (
    <div className="w-fit rounded-full bg-emerald-100 px-2 dark:bg-emerald-900">
      <span className="font-medium text-emerald-600 dark:text-emerald-300">Guérie</span>
    </div>
  ) : (
    <div className="w-fit rounded-full bg-red-100 px-2 dark:bg-red-900">
      <span className="font-medium text-red-600 dark:text-red-300">Malade</span>
    </div>
  );
}

function DiseaseList(props) {
  const [diseases, setDiseases] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editedDisease, setEditedDisease] = useState(null);
  const navigate = useNavigate();
  const { calfId } = useParams();
  const calfID = props.calfID || calfId;

  const getDiseases = useCallback(() => {
    api
      .get("/health/calf/" + calfID)
      .then((response) => {
        setDiseases(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was an error getting the diseases!", error);
      });
  }, [calfID]);

  useEffect(() => {
    if (!AuthService.getCurrentUser()) {
      navigate("/login");
    } else {
      getDiseases();
    }
  }, [navigate, getDiseases]);

  if (isLoading) {
    return <div></div>;
  }

  // On a phone a row opens its own page; on desktop it opens the dialog instead.
  const handleRowClick = (disease) => {
    if (!isDesktopViewport()) return true;
    setEditedDisease(disease.pk_health);
    return false;
  };

  return (
    <div className="dark:text-white">
      {diseases.length === 0 ? (
        <EmptyState
          title="Ajoutez une maladie"
          description={'Il n\'y a aucune maladie enregistrée pour ce veau. Cliquez sur "Ajouter une maladie" pour commencer.'}
          actionLabel="Ajouter une maladie"
          actionTo={"/nouvelle_maladie/" + calfID}
        />
      ) : (
        <ListCard
          title="Maladies"
          addLabel="Ajouter une maladie"
          addTo={"/nouvelle_maladie/" + calfID}
          onAdd={() => setCreateOpen(true)}
        >
          {diseases.map((disease, index) => (
            <ListRow
              key={disease.pk_health}
              to={"/maladie/" + calfID + "/" + disease.pk_health}
              onDesktopClick={() => handleRowClick(disease)}
              isLast={index === diseases.length - 1}
            >
              <div className="flex w-2/5 flex-col">
                <span>{describe(disease)}</span>
                <span className="text-sm text-stone-400">
                  {new Date(disease.start_date).toLocaleString("default", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
              <div className="flex w-1/3 justify-center">
                <StatusBadge healed={Boolean(disease.end_date)} />
              </div>
              <div className="m-3 flex grow justify-end">
                <ChevronIcon />
              </div>
            </ListRow>
          ))}
        </ListCard>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Ajouter une maladie"
      >
        <DiseaseForm
          calfID={calfID}
          onDone={() => {
            setCreateOpen(false);
            getDiseases();
          }}
          onCancel={() => setCreateOpen(false)}
        />
      </Modal>

      <Modal
        open={editedDisease !== null}
        onClose={() => setEditedDisease(null)}
        title="Modifier une maladie"
      >
        <DiseaseForm
          calfID={calfID}
          diseaseId={editedDisease}
          onDone={() => {
            setEditedDisease(null);
            getDiseases();
          }}
          onCancel={() => setEditedDisease(null)}
        />
      </Modal>
    </div>
  );
}

export default DiseaseList;
